import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import Select from "@/components/Select/Select";
import GenericAlertModal from "@/components/AlertModal";

import { useAppTheme } from "@/utils/theme";
import { usePageStore } from "@/hooks/usePageStore";
import useDebounce from "@/hooks/useDebounce";
import { useArchiveRole, useRolesPaginated } from "@/hooks/useRoles";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";

import { RoleType } from "@/domain/rol.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { toTitleCase } from "@/utils/toTitleCase";
import { RoleTypeEnum } from "@/domain/roles.enum";
import RoleModal from "@/ui/modals/roles/RoleModal";

type RolesFilters = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string;
  type: string;
  subType: string;
};

const defaultFilters: RolesFilters = {
  sortField: "name",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
  type: "",
  subType: "",
};

const statusOptions = [
  { key: "ACTIVE", value: "Active" },
  { key: "INACTIVE", value: "Inactive" },
];

const typeOptions = [
  { key: RoleTypeEnum.STANDARD_SYSTEM_ROLE, value: "Standard system role" },
  { key: RoleTypeEnum.STANDARD_ACCOUNT_ROLE, value: "Standard account role" },
  { key: RoleTypeEnum.CUSTOM_ROLE, value: "Custom role" },
];

const RolesPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { showSnackBar } = useFeedBack();

  const [filters, setFilters] = useState<RolesFilters>(defaultFilters);
  const debouncedSearch = useDebounce({ value: filters.search, delay: 500 });

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleType | null>(null);

  const [genericAlert, setGenericAlert] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => new Object(),
  });

  const archiveRole = useArchiveRole();

  const {
    rolesPaginated,
    isLoadingRolesPaginated,
    isFetchingRolesPaginated,
    refetchRolesPaginated,
  } = useRolesPaginated({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    status: filters.status,
    type: filters.type,
    subType: filters.subType,
  });

  useEffect(() => {
    setPageTitle("Roles");
  }, []);

  const handleArchive = async (roleId: string) => {
    try {
      await archiveRole.mutateAsync(roleId, {
        onSuccess: () => {
          showSnackBar("The registry has been deleted.", "success");
          setGenericAlert((prev) => ({ ...prev, open: false }));
          refetchRolesPaginated();
        },
      });
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const rows = useMemo(() => {
    if (!rolesPaginated || rolesPaginated.list.length === 0) return [];

    return rolesPaginated.list.map((role, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => {
            setSelectedRole(role);
            setShowRoleModal(true);
          },
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => {
            setSelectedRole(role);
            setGenericAlert({
              open: true,
              title: "Delete",
              message: "Are you sure you want to delete this registry?",
              onConfirm: () => handleArchive(role._id),
            });
          },
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const actionsCount = Array.isArray(role.actions)
        ? role.actions.length
        : 0;

      return createDataRow(index, {
        name: <div>{role.name}</div>,
        type: (
          <div>
            {role.type ? toTitleCase(role.type.replace(/_/g, " ")) : "-"}
          </div>
        ),
        subType: (
          <div>
            {role.subType ? toTitleCase(role.subType.replace(/_/g, " ")) : "-"}
          </div>
        ),
        status: (
          <Chip
            size="small"
            color={role.status === "ACTIVE" ? "success" : "default"}
            label={
              <div className="text-xs">{toTitleCase(role.status || "")}</div>
            }
          />
        ),
        actions: (
          <Chip
            size="small"
            variant="outlined"
            label={
              <div className="text-xs">{`${actionsCount} action${actionsCount === 1 ? "" : "s"}`}</div>
            }
          />
        ),
        action: <ActionMenu id={role._id} options={actionMenu} />,
      });
    });
  }, [rolesPaginated, theme]);

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "25%",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
      width: "20%",
    },
    {
      id: "subType",
      isSortable: true,
      disablePadding: false,
      label: "Sub Type",
      align: "left",
      width: "18%",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "12%",
    },
    {
      id: "actions",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "left",
      width: "15%",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Action",
      align: "right",
    },
  ];

  const handleAddClick = () => {
    setSelectedRole(null);
    setShowRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setSelectedRole(null);
    refetchRolesPaginated();
  };

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Roles
                  </Typography>

                  <Box display="flex" gap="0.5rem">
                    <Button
                      size="medium"
                      onClick={handleAddClick}
                      variant="contained"
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        textAlign: "center",
                        height: "40px",
                        backgroundColor: theme.palette.primary.dark,
                        color: theme.palette.textMain.light,
                      }}
                      startIcon={<AddIcon />}
                    >
                      ADD
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: "0.5rem",
                    width: "100%",
                    flexDirection: {
                      xs: "column",
                      sm: "column",
                      md: "row",
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: theme.palette.surface.light2,
                      px: 2,
                      width: {
                        xs: "100%",
                        sm: "100%",
                      },
                    }}
                    className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
                  >
                    <InputBase
                      className="flex-1"
                      placeholder="Search role"
                      inputProps={{ "aria-label": "search" }}
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          search: e.target.value,
                          pageNumber: 0,
                        }));
                      }}
                      value={filters.search}
                    />
                    <SearchIcon className="text-icon-primary" />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      width: {
                        xs: "100%",
                        sm: "100%",
                      },
                      flexDirection: {
                        xs: "column",
                        sm: "row",
                        md: "row",
                      },
                    }}
                  >
                    <Select
                      handleChange={(value) => {
                        setFilters((prev) => ({
                          ...prev,
                          status: value as string,
                          pageNumber: 0,
                        }));
                      }}
                      data={statusOptions}
                      value={filters.status}
                      label="Status"
                      id="roles-status"
                      maxWidth={320}
                      showClearOption
                      fullWidth
                    />

                    <Select
                      handleChange={(value) => {
                        setFilters((prev) => ({
                          ...prev,
                          type: value as string,
                          pageNumber: 0,
                        }));
                      }}
                      data={typeOptions}
                      value={filters.type}
                      label="Type"
                      id="roles-type"
                      maxWidth={320}
                      showClearOption
                      fullWidth
                    />
                  </Box>
                </Box>

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={rolesPaginated?.totalCount}
                  isLoading={
                    isLoadingRolesPaginated || isFetchingRolesPaginated
                  }
                  onChangePage={(event, newPage) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: newPage,
                    }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      sortField: orderBy as string,
                      sortOrder: order === "asc" ? 1 : -1,
                    }));
                  }}
                  messageEmptyData="No roles to show."
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {showRoleModal ? (
        <RoleModal
          open={showRoleModal}
          onClose={handleCloseRoleModal}
          role={selectedRole}
        />
      ) : null}

      {genericAlert.open ? (
        <GenericAlertModal
          onCancel={() => setGenericAlert((prev) => ({ ...prev, open: false }))}
          onClick={() => {
            genericAlert.onConfirm();
          }}
          title={genericAlert.title}
          description={genericAlert.message}
          submitText="Confirm"
          cancelText="Cancel"
          theme={theme}
        />
      ) : null}
    </>
  );
};

export default RolesPage;
